const TEXTE_SOLDE = /totalement\s+(r[ée]gl[ée]e?|sold[ée]e?)/i;

const ordreTypes = {
  Devis: 1,
  Commande: 2,
  Livraison: 3,
  Facture: 4,
  Avoir: 5,
  Règlement: 6
};

export function normaliserCodeClient(valeur) {
  return String(valeur || "").trim().toUpperCase();
}

export function documentsPourCodeClient(documents, codeClient) {
  const code = normaliserCodeClient(codeClient);
  if (!code) return [];
  return (documents || []).filter(
    (document) => normaliserCodeClient(document?.donnees?.code_client) === code
  );
}

export function factureEstSoldee(document) {
  return TEXTE_SOLDE.test(String(document?.donnees?.statut || ""));
}

export function getSituationInterclub(documents, situationDirecte = null) {
  const liste = documents || [];
  const commandesAFacturer = liste.filter(
    (document) => document.type_document === "COMMANDE_A_FACTURER"
  );
  const facturesOuvertes = liste.filter(
    (document) => document.type_document === "FACTURE" && !factureEstSoldee(document)
  );
  const avoirs = liste.filter((document) => document.type_document === "AVOIR");
  const dates = liste
    .map((document) => new Date(document.derniere_observation || document.updated_at || ""))
    .filter((date) => !Number.isNaN(date.getTime()));

  if (situationDirecte) {
    const factures = new Map();
    (situationDirecte.ecritures || []).forEach((ligne) => {
      const numero = String(ligne.numero_piece || "").trim();
      if (!numero || !/^F/i.test(numero)) return;
      const courant = factures.get(numero) || { debit: 0, credit: 0, date: ligne.date, numero_commande: ligne.numero_commande };
      courant.debit += Number(ligne.debit || 0);
      courant.credit += Number(ligne.credit || 0);
      courant.date = courant.date || ligne.date;
      courant.numero_commande = courant.numero_commande || ligne.numero_commande;
      factures.set(numero, courant);
    });
    const facturesReellesOuvertes = [...factures.entries()]
      .map(([numero, valeur]) => ({
        id: `compte-${numero}`,
        donnees: {
          numero_facture: numero,
          numero_commande: valeur.numero_commande,
          date_facturation: valeur.date,
          montant_ttc: Math.max(0, valeur.debit - valeur.credit),
          statut: valeur.credit > 0 ? "Partiellement payée" : "Non payée"
        }
      }))
      .filter((facture) => facture.donnees.montant_ttc > 0.009);
    const paiements = liste.filter((document) => document.type_document === "PAIEMENT");
    return {
      compteRattache: true,
      nonFacture: commandesAFacturer.length > 0,
      nonPaye: facturesReellesOuvertes.length > 0,
      commandesAFacturer,
      facturesOuvertes: facturesReellesOuvertes,
      avoirs,
      paiements,
      derniereActualisation: situationDirecte.actualise_le,
      soldeDisponible: situationDirecte.solde !== null && situationDirecte.solde !== undefined,
      solde: situationDirecte.solde
    };
  }

  return {
    compteRattache: liste.length > 0,
    nonFacture: commandesAFacturer.length > 0,
    nonPaye: facturesOuvertes.length > 0,
    commandesAFacturer,
    facturesOuvertes,
    avoirs,
    derniereActualisation: dates.length
      ? new Date(Math.max(...dates.map((date) => date.getTime()))).toISOString()
      : null,
    soldeDisponible: false,
    solde: null,
    paiements: []
  };
}

function dateDocument(document) {
  const data = document?.donnees || {};
  return data.date_facturation || data.date_confirmation || data.date_creation || "";
}

export function evenementDepuisDocument(document) {
  const data = document.donnees || {};
  const typeSource = document.type_document;
  if (typeSource === "CLIENT" || typeSource === "COMMANDE_A_FACTURER") return null;

  if (typeSource === "DEVIS") {
    return {
      id: `interclub-${document.id}`,
      type: "Devis",
      date: data.date_creation,
      montantTTC: data.montant_ttc,
      commentaire: data.statut || "Devis Interclub",
      numero_document: data.numero_devis || document.identifiant_source,
      source: "INTERCLUB",
      statut: data.statut || ""
    };
  }
  if (typeSource === "COMMANDE") {
    return {
      id: `interclub-${document.id}`,
      type: "Commande",
      date: data.date_confirmation || data.date_creation,
      montantTTC: data.montant_commande,
      commentaire: data.statut || "Commande Interclub",
      numero_document: data.numero_commande || document.identifiant_source,
      source: "INTERCLUB",
      statut: data.statut || ""
    };
  }
  if (typeSource === "FACTURE" || typeSource === "AVOIR") {
    return {
      id: `interclub-${document.id}`,
      type: typeSource === "AVOIR" ? "Avoir" : "Facture",
      date: data.date_facturation,
      montantTTC: data.montant_ttc,
      commentaire: data.statut || (typeSource === "AVOIR" ? "Avoir Interclub" : "Facture Interclub"),
      numero_document: data.numero_facture || document.identifiant_source,
      numero_commande: data.numero_commande || "",
      source: "INTERCLUB",
      statut: data.statut || ""
    };
  }
  if (typeSource === "BL" || typeSource === "BON_LIVRAISON") {
    return {
      id: `interclub-${document.id}`,
      type: "Livraison",
      date: data.date_bl || data.date_livraison,
      montantTTC: data.montant_ttc || data.montant,
      commentaire: data.statut || "Livraison Interclub",
      numero_document: data.numero_bl || document.identifiant_source,
      numero_commande: data.numero_commande || "",
      source: "INTERCLUB",
      statut: data.statut || ""
    };
  }
  if (typeSource === "PAIEMENT") {
    return {
      id: `interclub-${document.id}`,
      type: "Règlement",
      date: data.date_paiement,
      montantTTC: data.montant_impute ?? data.montant,
      resteAPayer: data.reste_a_payer,
      commentaire: data.mode_reglement || "Paiement Interclub",
      numero_document: data.reference || document.identifiant_source,
      numeroFacture: String(data.numero_facture || data.numero_piece || "").trim(),
      numero_commande: data.numero_commande || "",
      source: "INTERCLUB",
      statut: data.statut || ""
    };
  }
  return null;
}

function nouveauBloc(cle, numeroCommande = "") {
  return { cle, numeroCommande, evenements: [], nonFacturee: false, dateTri: "", synthese: null };
}

const montantEvenements = (evenements, type) => evenements
  .filter((event) => event.type === type)
  .reduce((total, event) => total + Number(event.montantTTC || 0), 0);

export function synthetiserBlocCommande(bloc) {
  const montantCommande = montantEvenements(bloc.evenements, "Commande");
  const montantLivre = montantEvenements(bloc.evenements, "Livraison");
  const montantFacture = montantEvenements(bloc.evenements, "Facture");
  const avoirs = Math.abs(montantEvenements(bloc.evenements, "Avoir"));
  const montantRegle = montantEvenements(bloc.evenements, "Règlement");
  const factures = bloc.evenements.filter((event) => event.type === "Facture");
  const reglements = bloc.evenements.filter((event) => event.type === "Règlement");
  const resteParFacture = factures.reduce((total, facture) => {
    const rattaches = reglements.filter((reglement) => reglement.numeroFacture && reglement.numeroFacture === facture.numero_document);
    const restes = rattaches.map((reglement) => Number(reglement.resteAPayer)).filter(Number.isFinite);
    const regle = rattaches.reduce((somme, reglement) => somme + Number(reglement.montantTTC || 0), 0);
    return total + Math.max(0, restes.length ? Math.min(...restes) : Number(facture.montantTTC || 0) - regle);
  }, 0);
  const resteDu = Math.max(0, factures.length ? resteParFacture - avoirs : montantFacture - avoirs - montantRegle);
  const commande = bloc.evenements.find((event) => event.type === "Commande");
  let statut = "Enregistrée";
  if (montantLivre > 0) statut = "Livrée";
  if (bloc.nonFacturee) statut = "À facturer";
  if (montantFacture > 0) statut = "Facturée";
  if (montantRegle > 0 && resteDu > 0.009) statut = "Partiellement réglée";
  if (montantFacture > 0 && resteDu <= 0.009) statut = avoirs > 0 ? "Soldée / avoir" : "Réglée";
  if (/confirm/i.test(String(commande?.statut || commande?.commentaire || "")) && montantLivre <= 0 && montantFacture <= 0) statut = "Confirmée";
  return { montantCommande, montantLivre, montantFacture, avoirs, montantRegle, resteDu, statut };
}

export function construireBlocsCycle({ documents = [], devisCrm = [], actions = [] }) {
  const blocs = new Map();
  const commandes = documents.filter((document) => document.type_document === "COMMANDE");

  commandes.forEach((document) => {
    const numero = String(document.donnees?.numero_commande || document.identifiant_source || "").trim();
    const cle = `COMMANDE:${numero}`;
    const bloc = blocs.get(cle) || nouveauBloc(cle, numero);
    const evenement = evenementDepuisDocument(document);
    if (evenement) bloc.evenements.push(evenement);

    const montantLivre = Number(document.donnees?.montant_livre || 0);
    const blReels = documents.some((item) =>
      ["BL", "BON_LIVRAISON"].includes(item.type_document)
      && String(item.donnees?.numero_commande || "").trim() === numero
    );
    if (montantLivre > 0 && !blReels) {
      bloc.evenements.push({
        id: `situation-livraison-${document.id}`,
        type: "Livraison",
        date: document.donnees?.date_facturation || document.donnees?.date_confirmation || "",
        montantTTC: montantLivre,
        commentaire: "Situation livraison Interclub — détail des BL indisponible dans l’export V1",
        numero_document: "BL non détaillés",
        source: "INTERCLUB",
        agregat: true
      });
    }
    blocs.set(cle, bloc);
  });

  documents
    .filter((document) => document.type_document === "COMMANDE_A_FACTURER")
    .forEach((document) => {
      const numero = String(document.donnees?.numero_commande || document.identifiant_source || "").trim();
      const cle = `COMMANDE:${numero}`;
      const bloc = blocs.get(cle) || nouveauBloc(cle, numero);
      bloc.nonFacturee = true;
      if (!bloc.evenements.some((event) => event.type === "Commande")) {
        bloc.evenements.push({
          id: `interclub-afacturer-${document.id}`,
          type: "Commande",
          date: dateDocument(document),
          montantTTC: document.donnees?.montant_commande,
          commentaire: "Commande à facturer — source Interclub",
          numero_document: numero,
          source: "INTERCLUB",
          statut: "À facturer"
        });
      }
      blocs.set(cle, bloc);
    });

  documents
    .filter((document) => ["FACTURE", "AVOIR", "BL", "BON_LIVRAISON", "PAIEMENT"].includes(document.type_document))
    .forEach((document) => {
      const evenement = evenementDepuisDocument(document);
      if (!evenement) return;
      const numeroCommande = String(document.donnees?.numero_commande || "").trim();
      const cle = numeroCommande
        ? `COMMANDE:${numeroCommande}`
        : `SANS_COMMANDE:${document.type_document}:${document.identifiant_source}`;
      const bloc = blocs.get(cle) || nouveauBloc(cle, numeroCommande);
      bloc.evenements.push(evenement);
      blocs.set(cle, bloc);
    });

  documents
    .filter((document) => document.type_document === "DEVIS")
    .forEach((document) => {
      const evenement = evenementDepuisDocument(document);
      if (!evenement) return;
      const numeroCommande = String(document.donnees?.numero_commande || "").trim();
      const cle = numeroCommande ? `COMMANDE:${numeroCommande}` : `DEVIS_INTERCLUB:${evenement.numero_document}`;
      const bloc = blocs.get(cle) || nouveauBloc(cle, numeroCommande);
      bloc.evenements.push(evenement);
      blocs.set(cle, bloc);
    });

  devisCrm.forEach((devis) => {
    const numeroCommande = String(devis.numero_commande_interclub || "").trim();
    const cle = numeroCommande
      ? `COMMANDE:${numeroCommande}`
      : `DEVIS_CRM:${devis.numero_devis || devis.id}`;
    const bloc = blocs.get(cle) || nouveauBloc(cle, numeroCommande);
    bloc.evenements.push({
      id: `devis-crm-${devis.id}`,
      type: "Devis",
      date: devis.date_creation || devis.created_at?.slice(0, 10) || "",
      montantTTC: devis.total_ttc ?? devis.montant_ttc,
      commentaire: devis.statut || "Devis CRM",
      numero_document: devis.numero_devis || "",
      source: "CRM",
      statut: devis.statut || ""
    });
    blocs.set(cle, bloc);
  });

  actions
    .filter((action) => !String(action.source_interclub || "").startsWith("import_"))
    .forEach((action) => {
      const numero = String(action.numero_document || "").trim();
      const type = action.type === "Facturé" ? "Facture" : action.type === "Payé" ? "Règlement" : action.type;
      const cle = numero && ["Commande", "Livraison", "Facture", "Avoir", "Règlement"].includes(type)
        ? `COMMANDE:${numero}`
        : `ACTION:${action.id}`;
      const bloc = blocs.get(cle) || nouveauBloc(cle, numero);
      bloc.evenements.push({ ...action, type, source: "CRM", id: `action-${action.id}`, actionOriginale: action });
      blocs.set(cle, bloc);
    });

  return [...blocs.values()]
    .map((bloc) => {
      bloc.evenements = bloc.evenements
        .sort((a, b) => {
          const ordre = (ordreTypes[a.type] || 99) - (ordreTypes[b.type] || 99);
          return ordre || String(a.date || "").localeCompare(String(b.date || ""));
        });
      bloc.dateTri = bloc.evenements.reduce(
        (max, event) => (String(event.date || "") > max ? String(event.date || "") : max),
        ""
      );
      bloc.synthese = synthetiserBlocCommande(bloc);
      return bloc;
    })
    .filter((bloc) => bloc.evenements.length > 0)
    .sort((a, b) => b.dateTri.localeCompare(a.dateTri));
}
